import { useState, useEffect } from 'react';
import Button from '../UI/Button/Button';
import SingleSlider from '../UI/SingleSlider/SingleSlider';
import { api } from '../../utils/api';
import styles from './MortgageApplication.module.css';
import cn from 'classnames';
import { sendLeadToWhatsApp } from '../../utils/whatsapp';

export default function ApplicantForm() {
  const [sliderValue, setSliderValue] = useState(4000000);
  const [depositValue, setDepositValue] = useState(Math.floor(4000000 * 0.3));
  const [monthsValue, setMonthsValue] = useState(50);
  const [step, setStep] = useState(1);
  useEffect(() => {
    const fetchMortgageData = async () => {
      try {
        const mortgageData = await api.mortgage.getAll();
        if (mortgageData && mortgageData.length > 0) {
          setMonthsValue(mortgageData[0].period);
        }
      } catch (error) {
        console.error('Ошибка при получении данных о рассрочке:', error);
      }
    };

    fetchMortgageData();
  }, []);

  const handleSliderChange = (newValue: number) => {
    setSliderValue(newValue);
    
    const newDeposit = Math.round((depositValue / sliderValue) * newValue);
    setDepositValue(newDeposit);
  };

  const handleDepositChange = (newValue: number) => {
    const minDeposit = Math.floor(sliderValue * 0.3);
    const maxDeposit = Math.floor(sliderValue * 0.8);

    const roundedValue = Math.round(newValue);

    if (roundedValue < minDeposit) {
      setDepositValue(minDeposit);
    } else if (roundedValue > maxDeposit) {
      setDepositValue(maxDeposit);
    } else {
      setDepositValue(roundedValue);
    }
  };

  const handleMonthsChange = (newValue: number) => {
    setMonthsValue(newValue);
  };

  const [formData, setFormData] = useState({
    fullName: '',
    phoneNumber: '',
  });

  const [errors, setErrors] = useState<{ [key: string]: boolean }>({});

  const validate = () => {
    const newErrors: { [key: string]: boolean } = {};

    if (!formData.fullName.trim()) newErrors.fullName = true;
    if (
      !formData.phoneNumber.trim() ||
      formData.phoneNumber.replace(/\D/g, '').length < 11
    )
      newErrors.phoneNumber = true;

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (validate()) {
      setErrors({});
      sendLeadToWhatsApp({
        title: 'Заявка на рассрочку',
        fields: [
          { label: 'Имя и фамилия', value: formData.fullName },
          { label: 'Телефон', value: formData.phoneNumber },
          { label: 'Стоимость', value: `${formatValue(sliderValue)} ₽` },
          { label: 'Первый взнос', value: `${formatValue(depositValue)} ₽` },
          { label: 'Срок', value: `${monthsValue} мес.` },
        ],
      });
      setStep(3);
    }
  };

  const formatPhoneNumber = (value: string) => {
    const digits = value.replace(/\D/g, '');

    if (digits === '' || digits === '+') {
      return '+7 ';
    }

    let formatted = '+7';
    if (digits.length > 1) formatted += ` ${digits.slice(1, 4)}`;
    if (digits.length > 4) formatted += ` ${digits.slice(4, 7)}`;
    if (digits.length > 7) formatted += `-${digits.slice(7, 9)}`;
    if (digits.length > 9) formatted += `-${digits.slice(9, 11)}`;

    return formatted;
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: name === 'phoneNumber' ? formatPhoneNumber(value) : value,
    }));
  };

  const handlePhoneFocus = (e: React.FocusEvent<HTMLInputElement>) => {
    if (!e.target.value.startsWith('+7')) {
      setFormData((prev) => ({ ...prev, phoneNumber: '+7 ' }));
    }
  };

  const handlePhoneBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    if (e.target.value === '+7 ' || e.target.value === '+7') {
      setFormData((prev) => ({ ...prev, phoneNumber: '' }));
    }
  };

  const getInputClassName = (fieldName: string) =>
    cn('text_subheaders_sh3', styles.form_field, errors[fieldName] && styles.error_field);

  const formatValue = (value: number): string => {
    return value.toLocaleString('ru-RU');
  };

  const getPaymentWord = (months: number) => {
    const lastDigit = months % 10;
    const lastTwoDigits = months % 100;

    if (lastTwoDigits >= 11 && lastTwoDigits <= 19) {
      return 'платежей';
    }
    if (lastDigit === 1) {
      return 'платеж';
    }
    if (lastDigit >= 2 && lastDigit <= 4) {
      return 'платежа';
    }
    return 'платежей';
  };



  return (
    <div className={styles.application_wrapper}>
      {step === 1 && (
        <div className={styles.application_info}>
          <div className={styles.application_head}>
            <h3>
              {monthsValue} ежемесячных{' '}
              {getPaymentWord(monthsValue)} {' '}
              <span className={styles.application_price}>
                по{' '}
                <span>
                  {formatValue(
                    Math.floor((sliderValue - depositValue) / monthsValue)
                  )}
                </span>{' '}
                ₽
              </span>
            </h3>
          </div>
          <div className={styles.sliders_wrapper}>
            <SingleSlider
              min={4000000}
              max={16000000}
              value={sliderValue}
              onChange={handleSliderChange}
              label="СТОИМОСТЬ"
              formatValue={(val) =>
                val.toLocaleString('ru-RU', {
                  useGrouping: true,
                  minimumFractionDigits: 0,
                })
              }
            />
            <SingleSlider
              min={Math.floor(sliderValue * 0.3)}
              max={Math.floor(sliderValue * 0.8)}
              value={depositValue}
              onChange={handleDepositChange}
              percent={Math.floor((depositValue / sliderValue) * 100)}
              label="ПЕРВЫЙ ВЗНОС"
              formatValue={(val) =>
                val.toLocaleString('ru-RU', {
                  useGrouping: true,
                  minimumFractionDigits: 0,
                })
              }
            />
            <SingleSlider
              min={6}
              max={50}
              value={monthsValue}
              onChange={handleMonthsChange}
              label="СРОК В МЕСЯЦАХ"
              step={1}
              formatValue={(val) => val.toString()}
            />
          </div>
          <div className={styles.button_wrapper}>
            <Button
              colorType="blue"
              roundType="small_rounded"
              type="submit"
              onClick={() => setStep(2)}
            >
              Оформить рассрочку
            </Button>
            <div className={cn('text_captions_c1', styles.underbutton_span)}>
              <span>
                При досрочном погашении&nbsp;&mdash; скидка. Подробности
                у&nbsp;менеджера. Не&nbsp;является публичной офертой.
              </span>
            </div>
          </div>
        </div>
      )}
      {step === 2 && (
        <div className={styles.form_wrapper}>
          <h3>Сперва надо заполнить</h3>
          <p className="text_body_b2">
            Достаточно ввести свои данные и&nbsp;мы&nbsp;обязательно свяжемся,
            чтобы детальнее рассказать о&nbsp;рассрочке.
          </p>
          <form className={styles.applicant_form_wrapper} onSubmit={handleSubmit}>
            <div>
              <input
                className={getInputClassName('fullName')}
                name="fullName"
                type="text"
                placeholder="Имя и фамилия"
                value={formData.fullName}
                onChange={handleInputChange}
              />

              <input
                className={getInputClassName('phoneNumber')}
                name="phoneNumber"
                type="text"
                placeholder="Телефон"
                value={formData.phoneNumber}
                onChange={handleInputChange}
                onFocus={handlePhoneFocus}
                onBlur={handlePhoneBlur}
              />
            </div>

            <Button colorType="blue" roundType="small_rounded" type="submit">
              Отправить
            </Button>
          </form>
        </div>
      )}
      {step === 3 && (
        <div className={styles.confirmation_wrapper}>
          <div className={styles.confirmation_text}>
            <div className={styles.verification}></div>

            <h3>Заявка отправлена.<br/>Скоро мы с вами свяжемся</h3>
          </div>
          <Button
            colorType="blue"
            roundType="small_rounded"
            type="submit"
            onClick={() => setStep(1)}
          >
            К калькулятору
          </Button>
        </div>
      )}
    </div>
  );
}
